// Shows a dismissible "install this app" banner on mobile.
//
// Android/Chrome fires beforeinstallprompt and gives us a real prompt() call.
// iOS Safari has no such API at all, so it gets a text hint instead.

import isStandalone from "./isStandalone";

const DISMISSED_KEY = "tgmq.installDismissed";

// The banner's colours now come from .install-banner in resets.css, which uses
// --accent-deep (#C43C00) for exactly the reason this constant used to exist:
// the brand orangered (#FF4500) is only 3.4:1 against white, under the 4.5:1 AA
// minimum for text this size, while #C43C00 is 5.3:1. Do not swap the class for
// --accent.

// Safari with cookies blocked throws SecurityError on any localStorage access,
// and Private Browsing throws on write. This banner is cosmetic; an unguarded
// throw here would propagate out of the entry module and leave the quiz page
// with no sign-in button and no auth observer.
function wasDismissed() {
  try {
    return !!localStorage.getItem(DISMISSED_KEY);
  } catch {
    return false;
  }
}

function rememberDismissal() {
  try {
    localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    // Private mode. The banner simply comes back on the next load.
  }
}

function isIOS() {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports a Macintosh user agent. maxTouchPoints separates a real
  // iPad from a desktop Mac.
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (/macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1)
  );
}

function showBanner(text, actionLabel, onAction) {
  // beforeinstallprompt can fire more than once per page load, after the
  // startup checks have already run, so both are re-checked here.
  if (document.getElementById("installBanner")) return;
  if (wasDismissed()) return;

  const banner = document.createElement("div");
  banner.id = "installBanner";
  // Normal flow at the top of the page, matching getOfflineMessage. A fixed
  // bottom bar would sit over whatever the page ends with — on play.html the
  // fourth option, or the Claim Your Certificate button. play.css makes body
  // the flex container precisely so this banner is a flex sibling: it takes its
  // own height and #flex-container gives that height up, rather than the banner
  // pushing a full-viewport box down past the bottom of the screen.
  banner.className = "install-banner";

  function removeBanner() {
    banner.remove();
  }

  const msg = document.createElement("span");
  msg.textContent = text;
  // .install-banner-msg states its own colour rather than inheriting. index.css
  // used to carry a bare `span` rule, which beat the banner's own colour and
  // painted this text orangered on orangered.
  msg.className = "install-banner-msg";
  banner.appendChild(msg);

  if (actionLabel) {
    const actionBtn = document.createElement("button");
    actionBtn.textContent = actionLabel;
    actionBtn.className = "install-banner-action";
    actionBtn.addEventListener("click", () => {
      removeBanner();
      onAction();
    });
    banner.appendChild(actionBtn);
  }

  const dismissBtn = document.createElement("button");
  dismissBtn.setAttribute("aria-label", "Dismiss");
  dismissBtn.textContent = "×";
  dismissBtn.className = "install-banner-dismiss";
  dismissBtn.addEventListener("click", () => {
    removeBanner();
    rememberDismissal();
  });
  banner.appendChild(dismissBtn);

  document.body.insertBefore(banner, document.body.firstChild);
}

export default function getInstallPrompt() {
  // Desktop, already installed, or already dismissed: nothing to do.
  if (!window.matchMedia("(pointer: coarse)").matches) return;
  if (isStandalone()) return;
  if (wasDismissed()) return;

  if (isIOS()) {
    showBanner("Install the quiz: tap Share, then Add to Home Screen.");
    return;
  }

  window.addEventListener("beforeinstallprompt", event => {
    event.preventDefault();
    // showBanner removes the banner before running this, so prompt() is called
    // with the banner already gone.
    showBanner("Add the quiz to your home screen.", "Install", () =>
      // Chrome rejects prompt() if it has already been used or the gesture
      // expired. The banner is gone by now and Chrome will not re-fire
      // beforeinstallprompt for this load, so there is nothing to recover to —
      // but an unhandled rejection helps no one.
      event.prompt().catch(error => console.log("Install prompt failed:", error))
    );
  });
}
